import React, { ReactNode, createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import { LOCAL_STORAGE_KEY } from "../constants";
import { getMe } from "../services/api/commonApi";
import { getToken } from "../utils/common/common";

export interface User {
  affiliate_user_code: number;
  affiliate_code: number;
  shop_code: number;
  username: string;
  is_admin: boolean;
  status: number;
  created_u_code: number;
  updated_u_code: number;
  pref?: string;
  affiliate_name?: string;
  has_invoice_registration?: boolean;
  account_type?: "affiliate" | "broker";
  affiliate_type?: "affiliate" | "broker";
  broker_profile_completed?: boolean;
  broker_access?: {
    issuer_affiliate_name?: string;
    can_view_initial_guarantee_invoice?: boolean;
    issuer_affiliates?: Array<{
      affiliate_code: number;
      affiliate_name: string;
      shop_code: number;
      shop_name: string;
    }>;
    allowed_rooms?: Array<{
      broker_access_code: number;
      building_code: number;
      building_name: string;
      room_code: number | null;
      room_name: string;
      fee_settlement_recipient_type: string;
      is_delegated?: boolean;
    }>;
    can_use_delegated_contract_operations?: boolean;
  } | null;
  password_warning?: {
    is_expierd: boolean;
    message: string;
    days_overdue: number;
    password_changed_at: string;
  };
}

type AuthStatus = "loading" | "authenticated" | "unauthenticated";

interface AuthContextType {
  user: User | null;
  authStatus: AuthStatus;
  hasToken: boolean;
  isAuthenticated: boolean;
  fetchUser: () => Promise<User | null>;
  setAccessToken: (token: string | null) => void;
  clearAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [hasToken, setHasToken] = useState<boolean>(() => !!getToken());
  const [authStatus, setAuthStatus] = useState<AuthStatus>(() => (getToken() ? "loading" : "unauthenticated"));
  const fetchUserPromiseRef = useRef<Promise<User | null> | null>(null);

  const clearAuth = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEY.ACCESS_TOKEN);
    setHasToken(false);
    setUser(null);
    setAuthStatus("unauthenticated");
  }, []);

  const setAccessToken = useCallback(
    (token: string | null) => {
      if (!token) {
        clearAuth();
        return;
      }

      localStorage.setItem(LOCAL_STORAGE_KEY.ACCESS_TOKEN, token);
      setHasToken(true);
      setUser(null);
      setAuthStatus("unauthenticated");
    },
    [clearAuth]
  );

  const fetchUser = useCallback(async () => {
    if (fetchUserPromiseRef.current) {
      return fetchUserPromiseRef.current;
    }

    const token = getToken();
    if (!token) {
      clearAuth();
      return null;
    }

    setHasToken(true);
    setAuthStatus("loading");

    fetchUserPromiseRef.current = (async () => {
      try {
        const userData = await getMe();
        setUser(userData);
        setAuthStatus("authenticated");
        return userData;
      } catch (error: any) {
        console.error("Failed to fetch user:", error);

        if (error?.response?.status === 401) {
          clearAuth();
        } else if (getToken()) {
          setAuthStatus("unauthenticated");
        } else {
          clearAuth();
        }

        return null;
      } finally {
        fetchUserPromiseRef.current = null;
      }
    })();

    return fetchUserPromiseRef.current;
  }, [clearAuth]);

  useEffect(() => {
    if (getToken()) {
      void fetchUser();
    }
  }, [fetchUser]);

  const value = {
    user,
    authStatus,
    hasToken,
    isAuthenticated: authStatus === "authenticated",
    fetchUser,
    setAccessToken,
    clearAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
